import { Injectable, OnDestroy } from '@angular/core';
import { CONSTANTS } from '../../../Constants/Constants';
import { ReportPageOrientation } from '../../../models/reports/ReportPageOrientation';
import { ReportList } from '../../../models/dtos/Enums/ReportList';
import { TranslateService } from '@ngx-translate/core';
import { IReportData } from '../../../models/reports/IReportData';
import { CommonDataService } from '../../common-data.service';
import { CommonFunctionsService } from '../../common-functions.service';
import { CommonMetaDataService } from '../../common-meta-data.service';
import { ReportsCommonService } from '../reports-common/reports-common.service';
import { ITableData } from '../../../models/reports/ITableData';
import { IData } from '../../../models/reports/IData';
import { IDataItem } from '../../../models/reports/IDataItem';
import { IFooter } from '../../../models/reports/IFooter';
import { IHeader } from '../../../models/reports/IHeader';
import { IndexDBService } from '../../common/indexDB.service';
import { Condition, EstimateSettings, LaborConstants, LaborData, LaborLevelwiseData, LaborRates, OtPwrSalesOffices, OverTimeAttributes, SalesOfficeLaborRates, SQWebArea, SQWebProject, SQWebSystem, Task, TypicalOfSystemHelper } from '../../../models/dtos';
import { SQWebAreaChildren } from '../../../models/SQWebAreaChildren';
import { ILaborByLevelReportInfo } from '../../../models/reports/ILaborByLevelReportInfo';
import { ILevelContext } from '../../../models/reports/ILevelContext';
import { roundToPrecision } from '../../../../types/number-extensions';
import { DecimalPrecisionPipe } from '../../../pipes/decimal-precision-pipe/decimal-precision.pipe';

type LaborFieldKey =
  | 'projectManagementLabor'
  | 'engineerLabor'
  | 'cyberSecEngineerLabor'
  | 'energyEngineerLabor'
  | 'lengEngineerLabor'
  | 'networkEngineerLabor'
  | 'qualificationEngineerLabor'
  | 'graphicsLabor'
  | 'programmingLabor'
  | 'specialistLabor'
  | 'digitalDeploymentLabor'
  | 'mechanicalEngineerLabor'
  | 'electricalEngineerLabor';

const DEFAULT_LEVEL_CONTEXT: ILevelContext = {
  otCollection: [],
  otCollectionDXR: null,
  isSystemDXR: false,
  isMaster: false,
  typicalOfCount: 0,
  typicalOfCountInArea: 0,
  typicalSystemUniqueId: '',
  parentAreaUniqueId: '',
  systemDifficulty: '',
};

@Injectable()
export class LaborByLevelService implements OnDestroy{

  private laborByLevelReportInfoList: ILaborByLevelReportInfo[] = [];
  private readonly laborByLevelReportInfoMap = new Map<string, ILaborByLevelReportInfo>();
  private businessNumber = 1;
  private salesOfficeLaborRates: SalesOfficeLaborRates;
  private readonly salesOfficeLaborRatesMap = new Map<number, LaborRates>();
  private readonly levelGroupedMap = new Map<string, ILaborByLevelReportInfo[]>();
  private readonly COL_WIDTH: string = '60';
  private estimateSettings: EstimateSettings;
  private laborConstants: LaborConstants;
  private typicalOfHelpers: Record<string, TypicalOfSystemHelper> = {};
  private readonly processedTypicalSystems = new Set<string>();
  // Typical-of groups whose DXR hours/cost are already in the report (counted once across areas).
  private readonly dxrCountedTypicalIds = new Set<string>();
  private laborLevelwiseData: LaborLevelwiseData;

  private readonly OT_PWR_CONFIG: Record<string, {
    laborField: LaborFieldKey;
    otRateField: keyof OverTimeAttributes;
    hasPwr: boolean;
    pwrRateField?: keyof LaborRates;
  }> = {
    PM: { laborField: 'projectManagementLabor', otRateField: 'overtimePMRate', hasPwr: false },
    ENG: { laborField: 'engineerLabor', otRateField: 'overtimeENGRate', hasPwr: false },
    CENG: { laborField: 'cyberSecEngineerLabor', otRateField: 'overtimeCENGRate', hasPwr: false },
    EENG: { laborField: 'energyEngineerLabor', otRateField: 'overtimeEENGRate', hasPwr: false },
    LENG: { laborField: 'lengEngineerLabor', otRateField: 'overtimeLENGRate', hasPwr: false },
    NENG: { laborField: 'networkEngineerLabor', otRateField: 'overtimeNENGRate', hasPwr: false },
    QENG: { laborField: 'qualificationEngineerLabor', otRateField: 'overtimeQENGRate', hasPwr: false },
    GRAP: { laborField: 'graphicsLabor', otRateField: 'overtimeGrapRate', hasPwr: false },
    PROG: { laborField: 'programmingLabor', otRateField: 'overtimeProgRate', hasPwr: false },
    SPEC: { laborField: 'specialistLabor', otRateField: 'overtimeSPECRate', hasPwr: true, pwrRateField: 'adjSpecPWRRate' },
    DDS: { laborField: 'digitalDeploymentLabor', otRateField: 'overtimeDdsRate', hasPwr: false },
    MECH: { laborField: 'mechanicalEngineerLabor', otRateField: 'overtimeMECHRate', hasPwr: true, pwrRateField: 'adjMechPWRRate' },
    ELEC: { laborField: 'electricalEngineerLabor', otRateField: 'overtimeELECRate', hasPwr: true, pwrRateField: 'adjElecPWRRate' },
  };

  private readonly LABOR_DISCIPLINES: { type: string; displayName: string; labelKey: string; taskField: LaborFieldKey; rateField: keyof LaborRates }[] = [
    { type: 'PM', displayName: 'Project Manager', labelKey: 'LABOR_BY_LEVEL_TYPE_PM', taskField: 'projectManagementLabor', rateField: 'projectManagementRates' },
    { type: 'ENG', displayName: 'Engineer', labelKey: 'LABOR_BY_LEVEL_TYPE_ENG', taskField: 'engineerLabor', rateField: 'engineeringRates' },
    { type: 'CENG', displayName: 'Cyber Sec Engineering', labelKey: 'LABOR_BY_LEVEL_TYPE_CENG', taskField: 'cyberSecEngineerLabor', rateField: 'cyberSecEngineeringRates' },
    { type: 'EENG', displayName: 'Energy Engineering', labelKey: 'LABOR_BY_LEVEL_TYPE_EENG', taskField: 'energyEngineerLabor', rateField: 'energyEngineeringRates' },
    { type: 'LENG', displayName: 'L Engineering', labelKey: 'LABOR_BY_LEVEL_TYPE_LENG', taskField: 'lengEngineerLabor', rateField: 'lEngineeringRates' },
    { type: 'NENG', displayName: 'Network Engineering', labelKey: 'LABOR_BY_LEVEL_TYPE_NENG', taskField: 'networkEngineerLabor', rateField: 'networkEngineeringRates' },
    { type: 'QENG', displayName: 'Qualification Engineering', labelKey: 'LABOR_BY_LEVEL_TYPE_QENG', taskField: 'qualificationEngineerLabor', rateField: 'qualificationEngineeringRates' },
    { type: 'PROG', displayName: 'Programming', labelKey: 'LABOR_BY_LEVEL_TYPE_PROG', taskField: 'programmingLabor', rateField: 'programmingRates' },
    { type: 'GRAP', displayName: 'Graphics', labelKey: 'LABOR_BY_LEVEL_TYPE_GRAP', taskField: 'graphicsLabor', rateField: 'graphicsRates' },
    { type: 'SPEC', displayName: 'Specialist', labelKey: 'LABOR_BY_LEVEL_TYPE_SPEC', taskField: 'specialistLabor', rateField: 'specialistRates' },
    { type: 'DDS', displayName: 'Digital Deployment Specialist', labelKey: 'LABOR_BY_LEVEL_TYPE_DDS', taskField: 'digitalDeploymentLabor', rateField: 'ddsRates' },
    { type: 'MECH', displayName: 'Mechanical', labelKey: 'LABOR_BY_LEVEL_TYPE_MECH', taskField: 'mechanicalEngineerLabor', rateField: 'mechanicalRates' },
    { type: 'ELEC', displayName: 'Electrical', labelKey: 'LABOR_BY_LEVEL_TYPE_ELEC', taskField: 'electricalEngineerLabor', rateField: 'electricianRates' }
  ];

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly reportsCommonService: ReportsCommonService,
    private readonly translate: TranslateService,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly indexDbService: IndexDBService,
    private readonly commonMetaDataService: CommonMetaDataService,
    private readonly decimalPrecisionPipe: DecimalPrecisionPipe
  ) { }

  async generateReport(reportId: ReportList, pageOrientation: ReportPageOrientation = CONSTANTS.REPORT_PAGEORIENTATION_PORTRAIT, internalReport: boolean = true) {
    const reportName = this.reportsCommonService.getSelectedReportName();
    const reportHeaderData = this.commonDataService.getReportHeaderData();
    const reportData: IReportData = {
      selectionName: this.translate.instant(reportName),
      reportTitle: this.translate.instant('LABOR_BY_LEVEL'),
      reportTitles: {
        data: this.reportsCommonService.generateReportTitles(reportHeaderData)
      },
      tableData: await this.generateTableData(),
      pageOrientation: pageOrientation,
      internalReport: internalReport
    }
    this.reportsCommonService.setSelectedReport(reportData);
  }

  async generateTableData(): Promise<ITableData[]> {
    this.laborByLevelReportInfoList = [];
    this.laborByLevelReportInfoMap.clear();
    this.levelGroupedMap.clear();
    this.businessNumber = 1;
    this.processedTypicalSystems.clear();
    this.dxrCountedTypicalIds.clear();
    const laborMetaData = this.commonDataService.getLaborMetaData();
    this.salesOfficeLaborRates = laborMetaData.salesOfficeLaborRates;
    this.laborConstants = laborMetaData.laborConstants;
    this.estimateSettings = this.commonDataService.getEstimateMetaData().estimateSettings;
    this.typicalOfHelpers = this.commonDataService.getTypicalOfHelpers();
    this.salesOfficeLaborRatesMap.clear();
    if (this.salesOfficeLaborRates) {
      for (const so of this.salesOfficeLaborRates) {
        this.salesOfficeLaborRatesMap.set(so.salesOfficeId, so);
      }
    }
    const sqWebProject = this.commonDataService.getSQWebProject();
    // Visibility: a hidden project/area/system (and everything under it) never contributes rows or hours.
    if (!sqWebProject.isItemVisible) {
      return [];
    }
    this.laborLevelwiseData = await this.indexDbService.getAllLaborData();
    if (!this.laborLevelwiseData.laborsCollection) {
      return [];
    }
    this.processProjectLevelData(this.laborLevelwiseData, sqWebProject);
    this.businessNumber++;
    this.processAllAreas(sqWebProject, this.laborLevelwiseData);
    this.calculateAndFilterRows();
    this.populateSummarySection();
    return this.buildTableData();
  }

  private processAllAreas(sqWebProject: SQWebProject, laborLevelwiseData: LaborLevelwiseData): void {
    if (sqWebProject.areas) {
      for (const area of sqWebProject.areas) {
        if (area.isItemVisible) {
          this.processAreaLaborData(area, laborLevelwiseData);
        }
      }
    }
  }

  private processProjectLevelData(laborLevelwiseData: LaborLevelwiseData, sqWebProject: SQWebProject): void {
    const laborData = laborLevelwiseData.laborsCollection[sqWebProject.itemUniqueId];
    this.processLaborDataForLevel(laborData, sqWebProject.itemUniqueId, 'Estimate Level');
  }

  private processAreaLaborData(area: SQWebArea, laborLevelwiseData: LaborLevelwiseData): void {
    const laborData = laborLevelwiseData.laborsCollection[area.itemUniqueId];
    this.processLaborDataForLevel(laborData, area.itemUniqueId, area.itemName);
    // Process area children
    for (const child of area.children) {
      const childItem = child as SQWebAreaChildren;
      // Hidden children are skipped before typical-of de-duplication in processSystemLaborData, so the
      // next visible member of a typical-of group is the one that gets reported.
      if (childItem.isItemVisible) {
        if (childItem.nodeType === CONSTANTS.NODETYPE_AREA) {
          this.processAreaLaborData(child as SQWebArea, laborLevelwiseData);
        } else if (childItem.nodeType === CONSTANTS.NODETYPE_BSETSYSTEM) {
          this.processSystemLaborData(childItem as SQWebSystem, area, laborLevelwiseData);
        }
      }
    }
  }

  /**
   * Whether this system supplies the DXR base hours and DXR OT/PWR cost (ctx.isMaster).
   * - Only a DXR master counts. For a typical-of group the master may not be the member listed
   *   first, so it is looked up across the whole group.
   * - A typical-of group's DXR data is one-time and sits once on the holder labor data, so it is
   *   claimed by the first area that reports the group and ignored in any other area.
   */
  private claimDxrMaster(system: SQWebSystem, typicalId: string | undefined): boolean {
    if (!system.isSystemDXR) {
      return false;
    }
    if (!typicalId) {
      return !!system.isMaster;
    }
    const group = this.typicalOfHelpers[typicalId]?.typicalOfSystems ?? [];
    const hasMaster = group.length ? group.some(member => member.isMaster) : !!system.isMaster;
    if (!hasMaster || this.dxrCountedTypicalIds.has(typicalId)) {
      return false;
    }
    this.dxrCountedTypicalIds.add(typicalId);
    return true;
  }

  private processSystemLaborData(system: SQWebSystem, area: SQWebArea, laborLevelwiseData: LaborLevelwiseData): void {
    // Resolve labor data ID — for typical systems, use master's ID
    let laborDataId = system.itemUniqueId;
    const typicalId = system.systemTypicalOfHelperUniqueId;
    let typicalOfCount = 0;
    let typicalOfCountInArea = 0;

    if (typicalId) {
      const helper = this.typicalOfHelpers[typicalId];
      if (helper?.typicalOfCollectionHolderId) {
        laborDataId = helper.typicalOfCollectionHolderId;
      }
      typicalOfCount = helper?.typicalOfSystems?.length ?? 0;
      typicalOfCountInArea = helper?.typicalOfSystems?.filter(
        s => s.parentUniqueId === area.itemUniqueId && s.isItemVisible
      ).length ?? 0;
    }

    // Typical-of: only process first occurrence per (typicalId + areaId)
    if (system.hasTypicalOf && typicalId) {
      const typicalAreaKey = `${typicalId}|${area.itemUniqueId}`;
      if (this.processedTypicalSystems.has(typicalAreaKey)) {
        return;
      }
      this.processedTypicalSystems.add(typicalAreaKey);
    }

    const levelId = system.hasTypicalOf && typicalId ? typicalId : system.itemUniqueId;
    const laborData = laborLevelwiseData.laborsCollection[laborDataId];

    // Build group header with typical-of prefix (matching Task By Level) plus in-area count suffix
    let groupHeader = (typicalOfCount > 0 && system.hasTypicalOf)
      ? `${CONSTANTS.TYPICAL_OF_LOGO} of ${typicalOfCount} ${system.itemName}`
      : system.itemName;
    if (typicalOfCountInArea > 0 && system.hasTypicalOf) {
      groupHeader += ` (${typicalOfCountInArea} ${this.translate.instant('IN_AREA')})`;
    }

    const systemDifficulty = this.getDifficultyDisplay(system.difficultyId);
    // ctx.isMaster means "this system supplies the DXR hours/cost" (see claimDxrMaster), not just system.isMaster.
    this.processLaborDataForLevel(laborData, levelId, groupHeader, {
      otCollection: [],
      otCollectionDXR: null,
      isSystemDXR: system.isSystemDXR ?? false,
      isMaster: this.claimDxrMaster(system, typicalId),
      typicalOfCount,
      typicalOfCountInArea,
      typicalSystemUniqueId: typicalId ?? '',
      parentAreaUniqueId: area.itemUniqueId,
      systemDifficulty,
    });
  }

  private processLaborDataForLevel(
    laborData: LaborData,
    levelId: string,
    groupHeaderName: string,
    ctx: ILevelContext = DEFAULT_LEVEL_CONTEXT
  ): void {
    if (!laborData) {
      return;
    }

    const firstRowIndex = this.laborByLevelReportInfoList.length;
    const otCollection = this.resolveOtCollection(laborData, ctx);
    const otCollectionDXR = this.resolveOtCollectionDXR(laborData, ctx);

    const resolvedCtx: ILevelContext = { ...ctx, otCollection: otCollection ?? [], otCollectionDXR };

    this.processTaskCollection(laborData.baseTasksCollection, true, levelId, groupHeaderName, resolvedCtx);
    const baseTaskRowsEndIndex = this.laborByLevelReportInfoList.length;

    // DXR base tasks: only when ctx.isMaster is set (the group's DXR master, first area only).
    // They are one-time work, so isDxrTasks stops the typical-of multiplier being applied to their hours.
    if (ctx.isSystemDXR && ctx.isMaster) {
      this.processTaskCollection(laborData.baseTasksDXRCollection, true, levelId, groupHeaderName, resolvedCtx, true);
    }

    this.processTaskCollection(laborData.tasksCollection, false, levelId, groupHeaderName, resolvedCtx);
    this.processTaskCollection(laborData.conditionsCollection, false, levelId, groupHeaderName, resolvedCtx);

    // Reorder only the base-task rows: by servicing office (first appearance in baseTasksCollection), then discipline order; unknowns go last.
    const baseOfficeOrder = new Map<number, number>();
    for (const task of laborData.baseTasksCollection ?? []) {
      if (!baseOfficeOrder.has(task.salesOfficeId)) {
        baseOfficeOrder.set(task.salesOfficeId, baseOfficeOrder.size);
      }
    }

    const officeOrder = (id: number): number => baseOfficeOrder.get(id) ?? Number.MAX_SAFE_INTEGER;
    const disciplineOrder = (laborTypeDisplayName: string): number => {
      const index = this.LABOR_DISCIPLINES.findIndex(discipline => discipline.displayName === laborTypeDisplayName);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    };
    const baseTaskRows = this.laborByLevelReportInfoList.splice(
      firstRowIndex,
      baseTaskRowsEndIndex - firstRowIndex
    );
    baseTaskRows.sort((a, b) =>
      officeOrder(a.servicingOfficeId) - officeOrder(b.servicingOfficeId)
      || disciplineOrder(a.laborType) - disciplineOrder(b.laborType)
    );

    // Keep first-seen base offices first; later-collection rows stay in encounter order.
    this.laborByLevelReportInfoList.splice(firstRowIndex, 0, ...baseTaskRows);
  }

  private resolveOtCollection(laborData: LaborData, ctx: ILevelContext): OtPwrSalesOffices[] {
    return this.commonDataService.globalPPMmode
      ? laborData.ppmSalesOfficesOtPwrAdjustmentsCollection
      : laborData.stdSalesOfficesOtPwrAdjustmentsCollection;
  }

  private resolveOtCollectionDXR(laborData: LaborData, ctx: ILevelContext): OtPwrSalesOffices[] | null {
    if (!ctx.isSystemDXR || !ctx.isMaster) {
      return null;
    }
    return this.commonDataService.globalPPMmode
      ? laborData.ppmSalesOfficesOtPwrAdjustmentsDXRCollection
      : laborData.stdSalesOfficesOtPwrAdjustmentsDXRCollection;
  }

  private processTaskCollection(
    collection: (Task | Condition)[] | null | undefined,
    isBaseHours: boolean,
    levelId: string,
    groupHeaderName: string,
    ctx: ILevelContext,
    isDxrTasks: boolean = false
  ): void {
    if (!collection?.length) {
      return;
    }
    for (const item of collection) {
      this.addUpdateListHours(item, isBaseHours, levelId, groupHeaderName, ctx, isDxrTasks);
    }
  }

  private addUpdateListHours(
    laborHourRow: Task | Condition,
    isBaseHours: boolean,
    levelId: string,
    groupHeaderName: string,
    ctx: ILevelContext,
    isDxrTasks: boolean = false
  ): void {
    for (const discipline of this.LABOR_DISCIPLINES) {
      const hours = laborHourRow[discipline.taskField] ?? 0;
      if (hours === 0) {
        continue;
      }

      // PROG/GRAP: only allow rows from EngOffice; DDS: only from ZineOffice
      if (!this.isReportRowAdditionAllowed(laborHourRow.salesOfficeId, discipline.type)) {
        continue;
      }

      const mapKey = `${levelId}|${laborHourRow.salesOfficeId}|${discipline.type}|${this.businessNumber}|${ctx.typicalSystemUniqueId}|${ctx.parentAreaUniqueId}`;
      let existingRow = this.laborByLevelReportInfoMap.get(mapKey);

      if (!existingRow) {
        existingRow = this.createNewReportRow(
          levelId, groupHeaderName, laborHourRow.salesOfficeId, discipline, ctx
        );
        this.laborByLevelReportInfoList.push(existingRow);
        this.laborByLevelReportInfoMap.set(mapKey, existingRow);

        // Calculate OT/PWR cost for this new row
        const costTypicalOf = ctx.typicalOfCountInArea === 0 ? 1 : ctx.typicalOfCountInArea;
        this.setOtPwrStdRateCostTotal(existingRow, discipline.type, ctx, costTypicalOf);
      }
      // DXR base hours are one-time: not multiplied by the typical-of count. This matches the DXR OT/PWR
      // cost in setOtPwrStdRateCostTotal, which also uses a count of 1, so hours and cost stay consistent.
      const hoursMultiplier = !isDxrTasks && ctx.typicalOfCountInArea > 0 ? ctx.typicalOfCountInArea : 1;
      if (isBaseHours) {
        existingRow.laborTypeBaseTaskHours += hours * hoursMultiplier;
      } else {
        existingRow.laborTypeTaskAdjustmentHours += hours * hoursMultiplier;
      }
    }
  }

  private createNewReportRow(
    levelId: string,
    groupHeaderName: string,
    salesOfficeId: number,
    discipline: { type: string; displayName: string },
    ctx: ILevelContext
  ): ILaborByLevelReportInfo {
    return {
      levelId,
      groupHeaderName,
      businessNumber: this.businessNumber,
      servicingOfficeId: salesOfficeId,
      servicingOffice: this.commonMetaDataService.getSalesOfficeDetails(salesOfficeId)?.salesOfficeName ?? '',
      laborType: discipline.displayName,
      laborDisciplineForReport: discipline.displayName,
      laborTypeBaseTaskHours: 0,
      laborTypeTaskAdjustmentHours: 0,
      laborDisciplineHoursTotal: 0,
      standardRate: 0,
      costTotal: 0,
      otPwrAdjustment: 'No',
      systemDifficulty: ctx.systemDifficulty,
      typicalOfValue: ctx.typicalOfCount === 0 ? '' : ctx.typicalOfCount.toString(),
      typicalOfSystemUniqueId: ctx.typicalSystemUniqueId,
      uniqueId: ctx.parentAreaUniqueId,
    };
  }

  /**
   * PROG/GRAP: only allow rows from EngOffice.
   * DDS (DDEP): only allow rows from ZineOffice.
   * All other disciplines: always allowed.
   */
  private isReportRowAdditionAllowed(salesOfficeId: number, disciplineType: string): boolean {
    if ((disciplineType === 'PROG' || disciplineType === 'GRAP') &&
        salesOfficeId !== this.estimateSettings.engOffice) {
      return false;
    }
    if (disciplineType === 'DDS' && salesOfficeId !== this.estimateSettings.zineOffice) {
      return false;
    }
    return true;
  }

  /**
   * Calculates cost using OT/PWR distribution and assigns standardRate and otPwrAdjustment.
   * PROG/GRAP use DefaultEngOffice for OT lookup; DDS uses DefaultZineOffice; others use servicingOfficeId.
   */
  private setOtPwrStdRateCostTotal(
    row: ILaborByLevelReportInfo,
    disciplineType: string,
    ctx: ILevelContext,
    typicalOfInArea: number
  ): void {
    if (!ctx.otCollection?.length) {
      this.setFallbackStdRate(row, disciplineType);
      return;
    }

    const officeIdForOtLookup = this.getOtLookupOfficeId(disciplineType, row.servicingOfficeId);
    const otPwrSalesOffice = ctx.otCollection.find(ot => ot.salesOfficeId === officeIdForOtLookup);
    const laborRates = this.salesOfficeLaborRatesMap.get(officeIdForOtLookup);

    if (otPwrSalesOffice?.overTimePWRAdjustments?.length && laborRates) {
      this.calculateDisciplineLaborCost(otPwrSalesOffice, laborRates, row, typicalOfInArea, disciplineType);

      // DXR master system: also add DXR OT costs with typicalOfInArea = 1
      if (ctx.isSystemDXR && ctx.isMaster && ctx.otCollectionDXR?.length) {
        const otPwrDXR = ctx.otCollectionDXR.find(ot => ot.salesOfficeId === officeIdForOtLookup);
        if (otPwrDXR?.overTimePWRAdjustments?.length) {
          this.calculateDisciplineLaborCost(otPwrDXR, laborRates, row, 1, disciplineType);
        }
      }
    } else {
      this.setFallbackStdRate(row, disciplineType);
    }
  }

  private getOtLookupOfficeId(disciplineType: string, servicingOfficeId: number): number {
    if (disciplineType === 'PROG' || disciplineType === 'GRAP') {
      return this.estimateSettings.defaultEngOffice;
    }
    if (disciplineType === 'DDS') {
      return this.estimateSettings.defaultZineOffice;
    }
    return servicingOfficeId;
  }

  private setFallbackStdRate(row: ILaborByLevelReportInfo, disciplineType: string): void {
    const salesOffice = this.salesOfficeLaborRatesMap.get(row.servicingOfficeId);
    const discipline = this.LABOR_DISCIPLINES.find(d => d.type === disciplineType);
    if (salesOffice && discipline) {
      row.standardRate = (salesOffice[discipline.rateField] as number) ?? 0;
    }
  }

  /**
   * Generic OT/PWR cost calculation for any discipline using index-based OT/PWR access.
   * Iterates the 8 OT/PWR tiers: Straight(0), OT1.1X(1), OT1.5X(2), OT2X(3),
   * PWR Straight(4), PWR1.1X(5), PWR1.5X(6), PWR2X(7).
   */
  private calculateDisciplineLaborCost(
    otPwrSalesOffice: OtPwrSalesOffices,
    laborRates: LaborRates,
    row: ILaborByLevelReportInfo,
    typicalOfInArea: number,
    disciplineType: string
  ): void {
    const config = this.OT_PWR_CONFIG[disciplineType];
    if (!config) {
      return;
    }
    const adj = otPwrSalesOffice.overTimePWRAdjustments;
    const discipline = this.LABOR_DISCIPLINES.find(d => d.type === disciplineType);
    const stdRate = discipline ? (laborRates[discipline.rateField] as number) ?? 0 : 0;
    const otRates = laborRates.stdOverTimeRates;
    let laborCost = 0;
    let otAdjustedHours = 0;

    // Straight time (index 0)
    const straightHours = adj[CONSTANTS.OT]?.[config.laborField] ?? 0;
    laborCost += straightHours * stdRate;

    // OT 1.1X (index 1)
    const ot11xHours = adj[CONSTANTS.OT_11X]?.[config.laborField] ?? 0;
    otAdjustedHours += ot11xHours;
    laborCost += ot11xHours * (otRates?.overTime11X?.[config.otRateField] ?? 0);

    // OT 1.5X (index 2)
    const ot15xHours = adj[CONSTANTS.OT_15X]?.[config.laborField] ?? 0;
    otAdjustedHours += ot15xHours;
    laborCost += ot15xHours * (otRates?.overTime15X?.[config.otRateField] ?? 0);

    // OT 2X (index 3)
    const ot2xHours = adj[CONSTANTS.OT_2X]?.[config.laborField] ?? 0;
    otAdjustedHours += ot2xHours;
    laborCost += ot2xHours * (otRates?.overTime2X?.[config.otRateField] ?? 0);

    // PWR cases (only SPEC, MECH, ELEC)
    if (config.hasPwr && config.pwrRateField) {
      const pwrRate = (laborRates[config.pwrRateField] as number) ?? 0;

      // PWR Straight (index 4)
      const pwrStraightHours = adj[CONSTANTS.PWR]?.[config.laborField] ?? 0;
      otAdjustedHours += pwrStraightHours;
      laborCost += pwrStraightHours * pwrRate;

      // PWR 1.1X (index 5)
      const pwr11xHours = adj[CONSTANTS.PWR_11X]?.[config.laborField] ?? 0;
      otAdjustedHours += pwr11xHours;
      laborCost += pwr11xHours * pwrRate * (this.laborConstants?.pwrShiftDifferential11X ?? 1);

      // PWR 1.5X (index 6)
      const pwr15xHours = adj[CONSTANTS.PWR_15X]?.[config.laborField] ?? 0;
      otAdjustedHours += pwr15xHours;
      laborCost += pwr15xHours * pwrRate * (this.laborConstants?.pwR15X ?? 1);

      // PWR 2X (index 7)
      const pwr2xHours = adj[CONSTANTS.PWR_2X]?.[config.laborField] ?? 0;
      otAdjustedHours += pwr2xHours;
      laborCost += pwr2xHours * pwrRate * (this.laborConstants?.pwR2X ?? 1);
    }

    row.costTotal += laborCost * typicalOfInArea;
    row.standardRate = stdRate;
    row.otPwrAdjustment = (row.otPwrAdjustment === 'Yes' || otAdjustedHours > 0) ? 'Yes' : 'No';
  }

  private getDifficultyDisplay(difficultyId: number | undefined): string {
    if (difficultyId === undefined) return '';
    if (difficultyId === 1) return 'System Difficulty Level: Simple';
    if (difficultyId === 2) return 'System Difficulty Level: Standard';
    return 'System Difficulty Level: Complex';
  }

  /**
   * Calculates LaborDisciplineHoursTotal and CostTotal for each row,
   * then removes rows where total hours are zero or negative — in a single pass.
   * Also rebuilds the levelGroupedMap for use by buildTableData.
   */
  private calculateAndFilterRows(): void {
    this.levelGroupedMap.clear();
    this.laborByLevelReportInfoList = this.laborByLevelReportInfoList.filter(row => {
      row.laborDisciplineHoursTotal = roundToPrecision(
        row.laborTypeBaseTaskHours + row.laborTypeTaskAdjustmentHours,
        this.commonDataService.getDecimalPrecision()
      );
      // costTotal is already calculated by OT/PWR calculation in setOtPwrStdRateCostTotal;
      // only fallback to simple rate * hours if costTotal is still 0 and we have hours
      if (row.costTotal === 0 && row.laborDisciplineHoursTotal !== 0) {
        row.costTotal = row.laborDisciplineHoursTotal * row.standardRate;
      }
      if (row.laborDisciplineHoursTotal > 0) {
        const group = this.levelGroupedMap.get(row.levelId);
        if (group) {
          group.push(row);
        } else {
          this.levelGroupedMap.set(row.levelId, [row]);
        }
        return true;
      }
      return false;
    });
  }

  /**
   * Creates summary rows by grouping all existing rows by (laborType, servicingOffice, servicingOfficeId).
   * Each summary row aggregates hours and cost from all levels for that discipline/office combination.
   * Summary rows have businessNumber=0 and groupHeaderName='Summary'.
   */
  private populateSummarySection(): void {
    const groups = new Map<string, ILaborByLevelReportInfo[]>();

    for (const row of this.laborByLevelReportInfoList) {
      const key = `${row.laborType}|${row.servicingOffice}|${row.servicingOfficeId}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(row);
    }

    // Order summary groups by servicing office ID, then discipline order (unknown types last, then by name).
    const orderedGroups = [...groups.values()].sort((a, b) => {
      const officeOrder = a[0].servicingOfficeId - b[0].servicingOfficeId;
      if (officeOrder !== 0) {
        return officeOrder;
      }
      const disciplineOrder = (laborType: string): number => {
        const index = this.LABOR_DISCIPLINES.findIndex(discipline => discipline.displayName === laborType);
        return index === -1 ? Number.MAX_SAFE_INTEGER : index;
      };
      return disciplineOrder(a[0].laborType) - disciplineOrder(b[0].laborType)
        || a[0].laborType.localeCompare(b[0].laborType);
    });

    for (const groupRows of orderedGroups) {
      if (groupRows.length === 0) {
        continue;
      } 

      const firstRow = groupRows[0];
      const summaryRow: ILaborByLevelReportInfo = {
        levelId: 'summary',
        groupHeaderName: 'Summary',
        businessNumber: 0,
        servicingOfficeId: firstRow.servicingOfficeId,
        servicingOffice: firstRow.servicingOffice,
        laborType: firstRow.laborType,
        laborDisciplineForReport: firstRow.laborDisciplineForReport,
        laborTypeBaseTaskHours: 0,
        laborTypeTaskAdjustmentHours: 0,
        laborDisciplineHoursTotal: 0,
        standardRate: firstRow.standardRate,
        costTotal: 0,
        otPwrAdjustment: groupRows.some(r => r.otPwrAdjustment === 'Yes') ? 'Yes' : 'No',
        systemDifficulty: '',
        typicalOfValue: '',
        typicalOfSystemUniqueId: '',
        uniqueId: ''
      };

      for (const row of groupRows) {
        summaryRow.standardRate = row.standardRate;
        summaryRow.laborTypeBaseTaskHours += row.laborTypeBaseTaskHours;
        summaryRow.laborTypeTaskAdjustmentHours += row.laborTypeTaskAdjustmentHours;
        summaryRow.laborDisciplineHoursTotal += row.laborDisciplineHoursTotal;
        summaryRow.costTotal += row.costTotal;
      }

      this.laborByLevelReportInfoList.push(summaryRow);
      const summaryGroup = this.levelGroupedMap.get('summary');
      if (summaryGroup) {
        summaryGroup.push(summaryRow);
      } else {
        this.levelGroupedMap.set('summary', [summaryRow]);
      }
    }
  }

  private buildTableData(): ITableData[] {
    const tableDataList: ITableData[] = [];

    // Build summary table first (businessNumber=0), then detail tables
    const summaryKey = 'summary';
    const orderedKeys: string[] = [];
    if (this.levelGroupedMap.has(summaryKey)) {
      orderedKeys.push(summaryKey);
    }
    for (const key of this.levelGroupedMap.keys()) {
      if (key !== summaryKey) {
        orderedKeys.push(key);
      }
    }

    for (const key of orderedKeys) {
      const rows = this.levelGroupedMap.get(key);
      const dataRows: IData[] = [];
      let totalBaseHours = 0;
      let totalAdjustmentHours = 0;
      let totalHours = 0;
      let totalCost = 0;

      const firstRow = rows[0];
      for (const row of rows) {
        dataRows.push(this.createDataRow(row));
        totalBaseHours += row.laborTypeBaseTaskHours;
        totalAdjustmentHours += row.laborTypeTaskAdjustmentHours;
        totalHours += row.laborDisciplineHoursTotal;
        totalCost += row.costTotal;
      }

      tableDataList.push({
        tableHeader: this.createGroupTableHeader(firstRow.groupHeaderName, firstRow.systemDifficulty),
        title: this.createTitleRow(),
        data: dataRows,
        tableFooter: this.createFooterRow(totalBaseHours, totalAdjustmentHours, totalHours, totalCost),
        isPageBreakAfter: false,
        isTableBordered: true
      });
    }

    return tableDataList;
  }

  /**
   * Group name banner; for system-level tables also shows the system difficulty label at the far right.
   */
  private createGroupTableHeader(groupName: string, systemDifficulty: string): IHeader[] {
    if (!systemDifficulty) {
      return this.reportsCommonService.generateReportTableHeader(groupName);
    }
    return [{
      headers: [
        { headerText: groupName, columnspan: 5 },
        { headerText: systemDifficulty, columnspan: 3, alignment: CONSTANTS.RIGHT_ALIGNMENT }
      ]
    }];
  }

  /**
   * Per-column headers; also drives colCount/widths for the whole table (see columnFilter.filterColumns).
   * The group name banner (Estimate Level/area/system) is rendered separately via tableHeader.
   */
  private createTitleRow(): IDataItem[] {
    const color = CONSTANTS.REPORT_LIGHT_BLUE_COLOR;
    return [
      { data: this.translate.instant('LABOR_TYPE'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color, alignment: CONSTANTS.LEFT_ALIGNMENT, width: '100' },
      { data: this.translate.instant('SERV_OFFICE'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color, alignment: CONSTANTS.LEFT_ALIGNMENT, width: '90' },
      { data: this.translate.instant('STD_RATE'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color, width: this.COL_WIDTH },
      { data: this.translate.instant('BASE_HOURS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color, width: this.COL_WIDTH },
      { data: this.translate.instant('ADJ_HOURS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color, width: this.COL_WIDTH },
      { data: this.translate.instant('HOURS_TOTAL'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color, width: this.COL_WIDTH },
      { data: this.translate.instant('OT_PWR'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color, width: this.COL_WIDTH },
      { data: this.translate.instant('COST_TOTAL'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color, width: this.COL_WIDTH }
    ];
  }

  private getLaborTypeLabel(displayName: string): string {
    const labelKey = this.LABOR_DISCIPLINES.find(d => d.displayName === displayName)?.labelKey;
    return labelKey ? this.translate.instant(labelKey) : displayName;
  }

  private createDataRow(row: ILaborByLevelReportInfo): IData {
    return {
      data: [
        { data: this.getLaborTypeLabel(row.laborType), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: row.servicingOffice, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: this.decimalPrecisionPipe.transform(row.standardRate, CONSTANTS.TWO), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { data: this.decimalPrecisionPipe.transform(row.laborTypeBaseTaskHours, CONSTANTS.TWO), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { data: this.decimalPrecisionPipe.transform(row.laborTypeTaskAdjustmentHours, CONSTANTS.TWO), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { data: this.decimalPrecisionPipe.transform(row.laborDisciplineHoursTotal, CONSTANTS.TWO), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { data: row.otPwrAdjustment, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: CONSTANTS.DOLLAR_SIGN + ' ' + this.decimalPrecisionPipe.transform(row.costTotal, CONSTANTS.TWO), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.RIGHT_ALIGNMENT }
      ]
    };
  }

  private createFooterRow(totalBaseHours: number, totalAdjustmentHours: number, totalHours: number, totalCost: number): IFooter[] {
    return [{
      headers: [
        { headerText: this.translate.instant('TOTALS'), columnspan: CONSTANTS.ONE },
        { headerText: '', columnspan: CONSTANTS.ONE },
        { headerText: '', columnspan: CONSTANTS.ONE },
        { headerText: this.decimalPrecisionPipe.transform(totalBaseHours, CONSTANTS.TWO), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { headerText: this.decimalPrecisionPipe.transform(totalAdjustmentHours, CONSTANTS.TWO), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { headerText: this.decimalPrecisionPipe.transform(totalHours, CONSTANTS.TWO), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { headerText: '', columnspan: CONSTANTS.ONE },
        { headerText: CONSTANTS.DOLLAR_SIGN + ' ' + this.decimalPrecisionPipe.transform(totalCost, CONSTANTS.TWO), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT }
      ]
    }];
  }

  ngOnDestroy(): void {
    this.laborByLevelReportInfoList = [];
    this.laborByLevelReportInfoMap.clear();
    this.salesOfficeLaborRatesMap.clear();
    this.levelGroupedMap.clear();
    this.processedTypicalSystems.clear();
    this.dxrCountedTypicalIds.clear();
  }
}
